import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { ManagedClient } from "../../shared/models/managed-client";
import { DacConfig } from "../../shared/services/dac-config.service";
import { AuthService } from 'app/login/services/auth.service';
import { ClientSearchService } from '../services/client-search.service';
import { AlertService } from 'app/shared/services/alert.service';
import { User } from 'app/shared/models/user';
import { ISubscription } from 'rxjs/Subscription';
import { Capability } from 'app/shared/models/capability';
import { Router, ActivatedRoute } from '@angular/router';
import { SubscriptionService } from '../services/subscription.service';
import { Role } from 'app/shared/models/role';

@Component({
    selector: 'app-client-list',
    templateUrl: './client-list.component.html',
    styleUrls: ['./client-list.component.css']
})
export class ClientListComponent implements OnInit,OnDestroy {

    private _clients: ManagedClient[];
    private _userSubscription:ISubscription;
    private _userHasActiveServiceSubscription:boolean;
    private _currentUser:User;
    public showTokens: Boolean;
    public searchFirstName: string;
    public searchLastName: string;
    public searchClientID: string;
    public searchEmail: string;
    public isSearching:boolean;
    public tokensEnabled:boolean;

    /**
     * Used for tracking with the name search is valid or not.
     */
    public searchByNameInvalid: boolean;

    constructor(
        private changeRef: ChangeDetectorRef,
        private authService: AuthService,
        private dacConfig: DacConfig,
        private clientSearchService: ClientSearchService,
        private alertService:AlertService,
        private router:Router,
        private subscriptionService:SubscriptionService,
        private route:ActivatedRoute
    ) {
        this._clients = [];
        this.tokensEnabled = false;
        this.isSearching = false;
        this._userSubscription = this.authService.getLoginState().subscribe(loginState => {
            this._currentUser = loginState.user;
            if (this.dacConfig.isFeatureEnabled("assessments.tokens.enabled")) {
                if (loginState.user && loginState.user.hasCapability(Capability.CAN_BUY_TOKENS)) {
                    this.tokensEnabled = true;
                }
            }
            if (loginState.user) {
                this._userHasActiveServiceSubscription = loginState.user.hasActiveSubscription();
            }
            // we don't allow show all users to work if they don't have permission to this.
            if (this._currentUser.role > Role.Admin) {
                this.clientSearchService.ShowAllUsers = false;
            }
        });
    }

    ngOnDestroy() {
        this._userSubscription.unsubscribe();
    }

    ngOnInit() {
        this.getDefaultClientList();
    }

    public get Clients(): ManagedClient[] {
        return this._clients;
    }

    public hasMoreResults(): boolean {
        return this.clientSearchService.hasMoreResults();
    }

    public get lastSearchValue() {
        return this.clientSearchService.SearchValue;
    }

    public addClient() {
        if (this.canAddClient) {
            this.router.navigate(['new'], {relativeTo: this.route});
        }
        else {
            this.subscriptionService.notifySubscriptionRequired();
        }
    }

    public get canAddClient() {
        return this.dacConfig.isFeatureEnabled("client.add") && this._userHasActiveServiceSubscription;
    }

    public get showAllUsers() {
        return this.clientSearchService.ShowAllUsers;
    }

    public get exactMatch() {
        return this.clientSearchService.ExactMatch;
    }

    public toggleAllUsers() {
        this.clientSearchService.ShowAllUsers = !this.clientSearchService.ShowAllUsers;
        this.resetSearch(); // clear out the search since we changed the toggle here.
    }

    public toggleExactMatch() {
        this.clientSearchService.ExactMatch = !this.clientSearchService.ExactMatch;
    }

    public doSearch() {
        this.searchByNameInvalid = false;
        let firstName = this.searchFirstName || "";
        let lastName = this.searchLastName || "";
        let clientID = this.searchClientID || "";
        let email = this.searchEmail || "";
        if (!(clientID || email) 
            && (firstName == "" || lastName == "")) {
            this.searchByNameInvalid = true;
            this.alertService.error("Missing client id, or first and last name to do search");
            return;
        }
        this.runSearch(firstName, lastName, clientID, email);
    }

    public resetSearch() {
        this.searchByNameInvalid = false;
        this.searchFirstName = "";
        this.searchLastName = "";
        this.searchClientID = "";
        this.searchEmail = "";
        this._clients = [];
        this.runSearch(this.searchFirstName, this.searchLastName, this.searchClientID, this.searchEmail);
    }

    private runSearch(firstName, lastName, clientID, email) {
        this.isSearching = true;
        let user = this.getUserForQuery();
        let search = {
            firstName: firstName.trim()
            ,lastName: lastName.trim()
            ,id: clientID.trim()
            ,email: encodeURIComponent(email.trim())
            ,exactMatch: this.exactMatch === true
        };
        this._clients = []; // clear our client list as we are doing a search.
        this.clientSearchService.search(search, user).then((results) => {
            this._clients = results;
            this.isSearching = false;
        })
        .catch((error) => this.handleSearchError(error));
    }

    public loadMore(): void {
        this.isSearching = true;
        this.clientSearchService.loadMore().then((results) => {
            this.isSearching = false;
            this._clients = results;
        })
        .catch(error => this.handleSearchError(error));
    }

    private getUserForQuery() {
        if (this.clientSearchService.ShowAllUsers) {
            return null;
        }
        return this._currentUser;
    }

    private getDefaultClientList() {
        let user = this.getUserForQuery();
        this.isSearching = true;

        this.clientSearchService.getClientList(user).then((results: ManagedClient[]) => {
            this.isSearching = false;
            this._clients = results;
            this.changeRef.detectChanges();
        })
        .catch(error => this.handleSearchError(error));
    }

    private handleSearchError(error) {
        this.alertService.error("There was an error retrieving the results from the server.  Please check your network connection and try again or contact support");
        console.error(error);
        this.isSearching = false;
    }
}



// WEBPACK FOOTER //
// ./src/app/admin/client-list/client-list.component.ts